import React from "react";

export type AlertVariant = "success" | "error" | "confirm";

type AlertModalProps = {
  show: boolean;
  variant?: AlertVariant;
  title?: string;
  message?: string;
  icon?: React.ReactNode;
  isLoading?: boolean;
  onConfirm: () => void;
  onConfirmText?: string;
  onCancel?: () => void;
  onCancelText?: string;
};

// Filled-shape icons (same treatment as the warningImg reference: a flat
// colored glyph, no gradients/glow — this theme is flat, not dark/neon).
const CheckIcon = ({ color }: { color: string }) => (
  <svg width="76" height="76" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="11" fill={color} />
    <path
      d="M7 12.5l3.2 3.2L17 9"
      stroke="#fff"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const WarningIcon = ({ color }: { color: string }) => (
  <svg width="76" height="76" viewBox="0 0 24 24" fill="none">
    <path d="M12 2.5L1.5 21h21L12 2.5z" fill={color} />
    <rect x="11" y="9" width="2" height="5.5" rx="1" fill="#fff" />
    <circle cx="12" cy="17" r="1.15" fill="#fff" />
  </svg>
);

const VARIANT_STYLES: Record<
  AlertVariant,
  { color: string; icon: (color: string) => React.ReactNode }
> = {
  success: { color: "#27ae60", icon: (c) => <CheckIcon color={c} /> },
  error: { color: "#e74c3c", icon: (c) => <WarningIcon color={c} /> },
  confirm: { color: "#e74c3c", icon: (c) => <WarningIcon color={c} /> },
};

const AlertModal = ({
  show,
  variant = "error",
  title,
  message,
  icon,
  isLoading,
  onConfirm,
  onConfirmText,
  onCancel,
  onCancelText,
}: AlertModalProps) => {
  if (!show) return null;

  const { color, icon: defaultIconFn } = VARIANT_STYLES[variant];
  const showCancel = Boolean(onCancel && onCancelText);

  return (
    <div style={styles.overlay}>
      <style>{spinnerKeyframes}</style>
      <div style={styles.card}>
        <div style={styles.imageWrap}>{icon ?? defaultIconFn(color)}</div>

        <h2 style={styles.title}>
          {title ?? (variant === "success" ? "Success" : "Subscription Failed")}
        </h2>

        {message && <p style={styles.subtitle}>{message}</p>}

        <div
          style={{
            ...styles.actions,
            flexDirection: showCancel ? "row" : "column",
          }}
        >
          {showCancel && (
            <button
              style={{ ...styles.outlineButton, borderColor: color, color }}
              onClick={onCancel}
              disabled={isLoading}
            >
              {onCancelText}
            </button>
          )}

          <button
            style={{
              ...styles.button,
              backgroundColor: color,
              flex: showCancel ? 1 : undefined,
            }}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <span style={styles.spinner} />
            ) : (
              (onConfirmText ?? "Ok")
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const spinnerKeyframes = `
@keyframes alert-modal-spin {
  to { transform: rotate(360deg); }
}
`;

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0, 0, 0, 0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: "20px",
    padding: "36px 32px 28px",
    maxWidth: "340px",
    width: "90%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
  },
  imageWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "20px",
  },
  title: {
    fontSize: "20px",
    fontWeight: 700,
    color: "#111",
    margin: "0 0 12px",
  },
  subtitle: {
    fontSize: "14px",
    color: "#555",
    lineHeight: 1.6,
    margin: "0 0 28px",
  },
  actions: {
    display: "flex",
    gap: 12,
    width: "100%",
  },
  button: {
    width: "100%",
    padding: "14px",
    color: "#fff",
    fontSize: "16px",
    fontWeight: 600,
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 48,
  },
  outlineButton: {
    flex: 1,
    padding: "14px",
    backgroundColor: "#fff",
    fontSize: "16px",
    fontWeight: 600,
    border: "1.5px solid",
    borderRadius: "12px",
    cursor: "pointer",
  },
  spinner: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    border: "2.5px solid rgba(255,255,255,0.4)",
    borderTopColor: "#fff",
    animation: "alert-modal-spin 0.7s linear infinite",
    display: "inline-block",
  },
};

export default AlertModal;
